import { useEffect, useState } from 'react';
import { DBDateFormat, DEFAULT_PAGINATION } from 'src/lib/constants';
import { AvailableDate, DetailedTimeslot, EnumOptionKind, Option, Pagination } from 'src/types';
import useEnhanceApiClient from './useEnhanceApiClient';
import ApiClient from 'src/lib/api_client';
import dayjs from 'dayjs';

const AVAILABILITY_PER_PAGE = 200;

const useOptionAvailabilitySlotFetcher = (
  option: Option,
  initialAvailableDates?: AvailableDate[],
  availableDatePagination?: Pagination
) => {
  const getAvailableDates = useEnhanceApiClient(ApiClient.getAvailableDates);
  const getDetailedTimeslots = useEnhanceApiClient(ApiClient.getDetailedTimeslots);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);

  const [detailedTimeslotsData, setDetailedTimeslotsData] = useState<{
    data: DetailedTimeslot[];
    pagination: Pagination;
  }>({ data: [], pagination: {} as Pagination });

  const [availableDatesData, setAvailableDatesData] = useState<{
    data: AvailableDate[];
    pagination: Pagination;
  }>({
    data: initialAvailableDates ? initialAvailableDates : [],
    pagination: availableDatePagination
      ? availableDatePagination
      : { ...DEFAULT_PAGINATION, totalCount: 1 },
  });

  const { data: availableDates } = availableDatesData;
  const { data: detailedTimeslots } = detailedTimeslotsData;

  const request = async (page: number, append?: boolean) => {
    const [data] = await getAvailableDates({
      page: page,
      per_page: AVAILABILITY_PER_PAGE,
      option_id: option.id,
      q: {
        actual_opened: 1,
        date_lteq: option.maxBookingDate,
        s: ['date asc'],
      },
    });
    if (append) {
      setAvailableDatesData((prev) => ({
        pagination: data?.pagination || prev.pagination,
        data: prev.data.concat(data?.data || []),
      }));
    } else {
      setAvailableDatesData({
        pagination: data?.pagination || {},
        data: data?.data || []
      });
    }
    return data
  };

  const request3 = async (page: number, append?: boolean) => {
    if (option.kind !== EnumOptionKind.shortAdventure) return;
    const [data] = await getDetailedTimeslots({
      page: page,
      per_page: AVAILABILITY_PER_PAGE,
      q: {
        supplier_activity_option_id_eq: option.id,
        date_lteq: option.maxBookingDate,
        date_gteq: dayjs().format(DBDateFormat),
        s: ['date asc', 'start_time asc'],
      },
    });
    if (append) {
      setDetailedTimeslotsData((prev) => ({
        pagination: data?.pagination || prev.pagination,
        data: prev.data.concat(data?.data || []),
      }));
    } else {
      setDetailedTimeslotsData({
        pagination: data?.pagination || {},
        data: data?.data || []
      });
    }
    return data
  };

  useEffect(() => {
    if (option) {
      request(1);
      request3(1);
    }
  }, [option?.id]);

  useEffect(() => {
    if (typeof availableDatesData.pagination?.totalPages !== 'number' || !option) {
      return;
    }
    const timeout = window.setTimeout(() => {
      const totalPages = availableDatesData.pagination.totalPages || 1;
      setIsLoadingAvailability(false);
      Promise.all([...Array(totalPages - 1)].map((_, index) => {
        return request(index + 2, true);
      })).then(() => {
        window.document.dispatchEvent(new CustomEvent('ss:trip_available_dates_fully_loaded', { detail: { option_id: option.id } }))
      })
      ;
    }, 500);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [option?.id, availableDatesData.pagination?.totalPages]);

  useEffect(() => {
    if (option?.kind !== EnumOptionKind.shortAdventure) return;
    if (typeof detailedTimeslotsData.pagination?.totalPages !== 'number') {
      return;
    }
    const timeout = window.setTimeout(() => {
      const totalPages = detailedTimeslotsData.pagination.totalPages || 1;
      [...Array(totalPages - 1)].map((_, index) => {
        return request3(index + 2, true);
      });
    }, 500);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [option?.id, detailedTimeslotsData.pagination?.totalPages]);

  return {
    availableDates: availableDates,
    availableDatesData: availableDatesData,
    detailedTimeslots: detailedTimeslots,
    detailedTimeslotsData: detailedTimeslotsData,
    isLoadingAvailability: isLoadingAvailability,
  };
};

export default useOptionAvailabilitySlotFetcher;
